/**
 * 授信环节 - 权益卡片展示
 * 设计稿地址：https://www.figma.com/design/AdVYyTUKFJh8MzcmYqbHIv/%E8%87%BB%E6%9C%89%E9%92%B1%E5%A4%A7%E4%BC%9A%E5%91%98?node-id=328-7699&m=dev
 * 主要功能：
 * 1. 根据后端接口返回情况判断权益卡片展示的样式，分为已购买 & 未购买
 * 2. 权益预下单接口封装
*/
import {
  Component, Prop, Vue, Watch,
} from 'vue-property-decorator';
import { namespace } from 'vuex-class';

import { getCreditBuyEquity, getSuperMemberCardSummary } from '@/api/rights';
import {
  BuyEquityResponseType, PackageBuyStatusEnum, EquityContractType, SuperMemberSourceEnum,
  RightPackageTypeEnum, NewSuperCardStyleTypeEnum, SuperMemberSceneEnum, SuperCardSummaryBackType,
} from '@/api/rights/types';
import { preCreateRightOrder } from '@/api/rights/order/order';
import { openInnerPage } from '@/api/native/utils';
import URLUtils from '@/shared/utils/URLUtils';
import { MembersRightsPrefix } from '@/constant';
import router from '@/router';
import store from '@/store';
import { ForceReadService, ForceReadServiceReturnType } from '@/widgets/right-contract';
import { ubtSendError } from '@/shared/utils/ubt';
import { ContractReadStatusEnum } from '@/widgets/right-contract/enum';

import style from './index.module.scss';
import '../../store';
import type { ApplyChooseList } from '../../types';

const PreApplyChooseStore = namespace('preApplyChoose');

/** 授信环节权益购买卡片 */
@Component({
  name: 'SuperMemberCard',
})
export default class SuperMemberCard extends Vue {
  @PreApplyChooseStore.State applyChooseList!: ApplyChooseList | null;

  @Prop(Object) readonly commoditys!: number[];

  @Prop() readonly from!: SuperMemberSourceEnum;

  @Watch('$route')
  private onRouteChange() {
    SuperMemberCard.instance = null;
  }

  private buyStatus: PackageBuyStatusEnum | null = null;

  private rightInfo: BuyEquityResponseType | SuperCardSummaryBackType | null = null;

  private checked = false;

  // 单例对象
  public static instance: SuperMemberCard | null;

  // 修改：从类属性改为私有属性，初始值为 null
  private preChooseContract: ForceReadServiceReturnType | null = null;

  // 新增：存储当前的packageType，用于判断是否需要重新初始化
  private currentPackageType: RightPackageTypeEnum | null = null;

  /** 埋点公用参数 */
  private get tqParams() {
    const {
      supplierCode, packageId, packageType, packageBuyStatus, sellPrice,
    } = this.rightInfo || {};
    return {
      supplierCode,
      sellPrice,
      packageId,
      packageType,
      from: this.from,
      productId: this.commoditys,
      packageBuyStatus,
    };
  }

  // 获取单例
  private static getInstance(element?: HTMLElement): SuperMemberCard | null {
    if (!SuperMemberCard.instance && element) {
      SuperMemberCard.instance = new SuperMemberCard({
        router,
        store,
      }).$mount(element);
    }

    return SuperMemberCard.instance;
  }

  public static async init(
    params: {
      commoditys: number[],
      /** 来源 */
      from: SuperMemberSourceEnum,
    },
    /** 实例挂载的DOM节点 */
    element: HTMLElement,

  ): Promise<void> {
    const { commoditys, from } = params;
    const instance = SuperMemberCard.getInstance(element);
    if (instance) {
      instance.$props.commoditys = commoditys;
      instance.$props.from = from;
      await instance.checkBuy();
    }
  }

  /** 由于该组件实例并未挂在到父组件DOM中，而是自身创建了一个实例变量，getInstance方法中只是把内容挂载在特定的节点中，并不会跟随所挂载节点div的销毁而被销毁
   * 所以父组件被卸载时，需要手动销毁实例，防止开发环境热更新情况组件不会再次被挂载问题
   */
  public static destroyInstance(): void {
    SuperMemberCard.instance = null;
  }

  /** 导出当前是否同意 & 需要购买会员 */
  public static needBuySuperMemberCard(): boolean {
    const instance = SuperMemberCard.getInstance();
    return !!instance?.checked && instance?.buyStatus === PackageBuyStatusEnum.NOT_BUY;
  }

  /** 导出当前会员卡片状态，供外部调用 */
  public static getSuperMember(): {
    isSuperMemberCardShow: boolean,
    buyStatus?: PackageBuyStatusEnum | null,
    hasBuy?: boolean
    } {
    if (SuperMemberCard.instance) {
      const { instance } = SuperMemberCard;
      return {
        isSuperMemberCardShow: instance && !!instance.buyStatus,
        buyStatus: instance && instance.buyStatus,
        hasBuy: instance && instance.buyStatus === PackageBuyStatusEnum.BOUGHT,
      };
    }

    return {
      isSuperMemberCardShow: false,
    };
  }

  /** 切换勾选状态 */
  private async switchCheck() {
    this.checked = !this.checked;
    // 勾选埋点
    if (this.checked) {
      this.$ubtPageButtonNew('finance_loan_packageEntry_agreementCheck', {
        ...this.tqParams,
      });

      // 修改：添加空值检查
      if (this.preChooseContract) {
        this.checked = await this.preChooseContract.handleForceRead({
          contractScene: this.rightInfo?.contractInfo?.scene || '',
          tqParams: this.tqParams,
        });
      }
    }
  }

  /** 判断是否需要购买权益 */
  private async checkBuy() {
    const creditProductList = this.applyChooseList?.commoditys || [];
    const productList = creditProductList.map((item) => ({
      productId: item.commodityId,
      followLoan: item.followLoan || false,
    }));

    try {
      if (this.from === SuperMemberSourceEnum.CREDIT_AGREEMENT) {
        this.rightInfo = await getCreditBuyEquity({
          productIds: this.commoditys,
          productList,
        });
      } else {
        this.rightInfo = await getSuperMemberCardSummary({
          sceneType: SuperMemberSceneEnum.LOAN_SUPERMARKET_AGREEMENT_SCENE,
        });
      }

      // 若不需要购买 接口无rightInfo数据返回
      this.buyStatus = this.rightInfo?.packageBuyStatus || null;

      // 新增：根据接口返回的packageType动态初始化preChooseContract
      if (this.rightInfo?.packageType) {
        const { packageType } = this.rightInfo;

        // 如果已初始化且packageType相同，则不需要重新初始化
        if (!this.preChooseContract || this.currentPackageType !== packageType) {
          // 销毁旧的实例
          if (this.preChooseContract) {
            this.preChooseContract.destroyInstance();
          }

          // 初始化新的实例
          this.preChooseContract = ForceReadService({
            packageType,
            forceReadFromScene: SuperMemberSourceEnum.CREDIT_AGREEMENT,
          });
          this.currentPackageType = packageType;
        }
      } else {
        // 兜底：如果接口返回的packageType为空，使用默认值
        if (!this.preChooseContract) {
          this.preChooseContract = ForceReadService({
            packageType: RightPackageTypeEnum.SUPER_MEMBER_CARD,
            forceReadFromScene: SuperMemberSourceEnum.CREDIT_AGREEMENT,
          });
          this.currentPackageType = RightPackageTypeEnum.SUPER_MEMBER_CARD;
        }
      }

      // 存在购买状态再去上报会员卡片的曝光，因后端存在卡片展示实验，如果未命中卡片展示则不需要上报
      if (this.buyStatus) {
        this.$ubtModuleExposeNew('finance_loan_packageEntry', {
          ...this.tqParams,
        });
      }
    } catch (err) {
      // catch error and report error
      this.$ubtPageButtonNew('finance_loan_credit_memberGetshow', { err: JSON.stringify(err) });
    }
  }

  /** 权益协议弹窗 */
  private async showContract() {
    const { contractInfo } = this.rightInfo || {};
    if (contractInfo && this.preChooseContract) {
      // 点击查看合同埋点
      this.$ubtPageButtonNew('finance_loan_packageEntry_agreement', {
        ...this.tqParams,
      });
      const handleRes = await this.preChooseContract.handleForceRead({
        contractScene: contractInfo.scene,
        tqParams: this.tqParams,
        preview: true,
      });
      if (handleRes) {
        this.checked = handleRes;
      }
    }
  }


  private goDetail() {
    const rightsUrl = URLUtils.formatUrl(
      `${MembersRightsPrefix}super-member.html#/super-member`,
      {
        from: `${this.from}_cjhy_detail`,
        /** 需求：https://metis2.hellobike.cn/team/detail/issue?filterId=57253&openDetail=633756&teamId=198 引入packageId，后续可看情况删除 */
        packageId: this.rightInfo?.packageId,
        packageType: this.rightInfo?.packageType,
        hiddenNavigationBar: 1,
      },
    );
    openInnerPage(rightsUrl);
    document.addEventListener('visibilitychange', () => {
      this.checkBuy();
    });
  }

  /** 销毁时移除监听 */
  private destroyed() {
    document.removeEventListener('visibilitychange', () => {
      this.checkBuy();
    });
    // 修改：添加空值检查
    if (this.preChooseContract) {
      this.preChooseContract.destroyInstance();
    }
  }

  /** 购买权益
   * @param taskId 授信任务id
  */
  public static async buyRights(taskId: string): Promise<{ orderId: string }> {
    const instance = SuperMemberCard.getInstance();
    const { packageId = '' } = instance?.rightInfo || {};
    // 是否为跟贷订单
    const followLoan = !!(instance?.applyChooseList?.commoditys.some((item) => item.followLoan));

    // 这里只处理打点，不做任何接口层面的二次校验
    if (instance?.preChooseContract?.readStatus === ContractReadStatusEnum.NEED_READ_NOT_READ) {
      ubtSendError({
        name: 'right_force_read',
        message: 'needReadContract but not read',
        stack: SuperMemberSourceEnum.CREDIT_AGREEMENT,
      });
    }

    const res = await preCreateRightOrder({
      packageId,
      creditTaskId: taskId,
      from: instance?.from || SuperMemberSourceEnum.CREDIT_AGREEMENT,
      followLoan,
    });
    return res;
  }

  /** 未购买样式测试 */
  private renderContent(
    title?: string,
    contractInfo?: EquityContractType,
    sellPrice?: string,
  ) {
    return this.renderContentStyle(title, contractInfo, sellPrice);
  }

  /** 渲染合同文案 */
  private renderContract(
    contractInfo?: EquityContractType,
  ) {
    if (contractInfo && Object.keys(contractInfo).length) {
      return (
        <div class={style.contractWrapper}>
          <div class={[
            style.superMemberCardCheck,
            this.checked ? style.checked : style.superMemberCardCheck,
          ]} onClick={this.switchCheck.bind(this)}>
          </div>
          <div class={style.superMemberCardContract} onClick={this.showContract.bind(this)}>
            我已阅读并同意<span>《{contractInfo.name}》</span>
          </div>
        </div>
      );
    }
    return null;
  }

  /** 根据packageType动态生成购买文案描述 */
  private getPurchaseDesc(): string | null {
    if (!this.rightInfo) {
      return null;
    }

    const { packageType, originalPrice = '' } = this.rightInfo;
    // 兼容两种字段名：sellerPrice 和 sellPrice
    const sellPrice = (this.rightInfo as BuyEquityResponseType).sellPrice
      || (this.rightInfo as SuperCardSummaryBackType).sellPrice
      || '';

    // 普通超会（packageType = 8）
    if (packageType === RightPackageTypeEnum.SUPER_MEMBER_CARD) {
      if (sellPrice) {
        return `自动续费${sellPrice}元/月可随时取消`;
      }
      return null;
    }

    // PLUS卡（packageType = 10）
    if (packageType === RightPackageTypeEnum.SUPER_MEMBER_PLUS_CARD) {
      if (sellPrice) {
        return `超级会员付费${sellPrice}元升级PLUS会员 后续自动续费${originalPrice}元/月`;
      }
      return null;
    }

    return null;
  }

  private renderContentStyle(
    title?: string,
    contractInfo?: EquityContractType,
    sellPrice?: string,
  ) {
    // 修改：使用getPurchaseDesc()方法获取动态文案
    const purchaseDesc = this.getPurchaseDesc();

    return (
      <div class={style.card}>
        <div class={style.cardWrapperStyle}>
          <div class={style.titleGroup}>
            <div class={[style.titleText]}>
              {title || '成为会员 立享返现红包权益'}
            </div>
            {purchaseDesc && (
              <div class={style.desc}>
                {purchaseDesc}
              </div>
            )}
          </div>
          <div class={style.icon}>
            <i class={style.clickIcon} onClick={this.goDetail.bind(this)}></i>
          </div>
        </div>
        {this.renderContract(contractInfo)}
      </div>
    );
  }

  private render() {
    const {
      sellPrice, contractInfo, title,
    } = this.rightInfo || {};


    return (
      this.buyStatus
        ? (<div class={[style.superMemberCard]}>
          {/** 未购买状态 */}
          {this.buyStatus === PackageBuyStatusEnum.NOT_BUY
            && <div class={style.superMemberCardNotBuy}>
              {this.renderContent(title, contractInfo, sellPrice)}
            </div>}
          {/** 购买状态 */}
          {/* {this.buyStatus === PackageBuyStatusEnum.BOUGHT
          && <div class={style.superMemberCardBought}>
          {title}
        </div>} */}
        </div>) : null
    );
  }
}
